<template>
	<v-row
		class="dashboard-row"
	>
		<!-- Time-series chart (stacked bar) -->
		<v-col
			:cols="fullWidth ? 12 : 12"
			:sm="fullWidth ? 12 : 8"
		>
			<ConsentChartCard
				:title="chart.title"
				icon="mdi-chart-bar"
				:height="360"
			>
				<template #header-actions>
					<!-- View Mode Toggle (hidden when single dataset) -->
					<v-btn-toggle
						v-if="showToggle"
						v-model="viewMode"
						mandatory
						dense
						class="view-mode-toggle"
						role="group"
						aria-label="Chart view mode selection"
					>
						<v-tooltip bottom>
							<template #activator="{ on, attrs }">
								<v-btn
									small
									:value="'instances'"
									:aria-label="`View as instances${viewMode === 'instances' ? ' (selected)' : ''}`"
									v-bind="attrs"
									v-on="on"
								>
									<span
										class="toggle-symbol"
										aria-hidden="true"
									>
										#
									</span>
								</v-btn>
							</template>
							<span>
								View as instances
							</span>
						</v-tooltip>
						<v-tooltip bottom>
							<template #activator="{ on, attrs }">
								<v-btn
									small
									:value="'percentage'"
									:aria-label="`View as percentage${viewMode === 'percentage' ? ' (selected)' : ''}`"
									v-bind="attrs"
									v-on="on"
								>
									<span
										class="toggle-symbol"
										aria-hidden="true"
									>
										%
									</span>
								</v-btn>
							</template>
							<span>
								View as percentage
							</span>
						</v-tooltip>
					</v-btn-toggle>

					<v-tooltip bottom>
						<template #activator="{ on, attrs }">
							<v-btn
								icon
								small
								v-bind="attrs"
								v-on="on"
								@click="$emit('expand', chart)"
							>
								<v-icon small>
									mdi-arrow-expand
								</v-icon>
							</v-btn>
						</template>
						<span>
							View detailed breakdown
						</span>
					</v-tooltip>
				</template>
				<BarChart
					:chart-data="barChart.chartData"
					:chart-options="barChart.chartOptions"
					:palette="palette"
					:color-version="colorVersion"
					:height="280"
				/>
			</ConsentChartCard>
		</v-col>

		<!-- Derived pie chart (hidden if fullWidth) -->
		<v-col
			v-if="!fullWidth"
			cols="12"
			sm="4"
		>
			<ConsentChartCard
				:title="pieChartTitle"
				icon="mdi-chart-pie"
				:height="360"
			>
				<div
					v-if="hasPieData"
					class="pie-chart-container"
				>
					<PieChart
						:chart-data="pieChartData"
						:chart-options="pieChartOptions"
					/>
				</div>
				<div
					v-else
					class="text-center text--secondary pa-4"
				>
					No data available
				</div>
			</ConsentChartCard>
		</v-col>
	</v-row>
</template>

<script>
import { derivePieData, toPieChartFormat } from '../../../../../shared/utils/derive-pie-data.js'
import { getChartColorByIndex } from '../../../../../shared/utils/getChartColorByIndex.js'
import ConsentChartCard from './consent-chart-card.vue'
import BarChart from '../../../../../shared/components/chart/bar-chart.vue'
import PieChart from '../../../../../shared/components/chart/pie-chart.vue'

export default {
	name: 'dashboard-row',
	components: {
		ConsentChartCard,
		BarChart,
		PieChart
	},
	props: {
		chart: {
			type: Object,
			required: true
		},
		palette: {
			type: String,
			default: 'default'
		},
		colorVersion: {
			type: Number,
			default: 0
		},
		fullWidth: {
			type: Boolean,
			default: false
		},
		hideToggle: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			viewMode: 'instances'
		}
	},
	computed: {
		/**
		 * Check if currently in percentage view mode
		 * @returns {boolean} True if percentage mode is active
		 */
		isPercentageMode () {
			return this.viewMode === 'percentage'
		},

		/**
		 * Check if toggle should be shown
		 * Hide toggle when explicitly disabled via prop or when there's only one dataset
		 * @returns {boolean} True if toggle should be displayed
		 */
		showToggle () {
			if (this.hideToggle) return false
			const datasetCount = this.chart?.chartData?.datasets?.length || 0
			return datasetCount > 1
		},

		/**
		 * Generates the bar chart configuration with data transformations.
		 *
		 * This computed property handles:
		 * - **Data Transformations**: Converts raw instance counts to percentages when
		 *   percentage mode is active via convertToPercentage()
		 * - **Theme Handling**: Detects dark/light mode and calculates appropriate
		 *   grid and tick colours for visibility
		 * - **Chart Configuration**: Configures Chart.js options including:
		 *   - Stacked bar layout (x and y axes)
		 *   - Y-axis max of 100 in percentage mode
		 *   - Custom tooltip formatters for instances vs percentages
		 *   - Rounded bar corners (borderRadius: 4)
		 * - **Options Merging**: Preserves incoming chartOptions (legend settings, etc.)
		 *   while applying required overrides for the stacked bar display
		 *
		 * @returns {Object|null} Chart.js configuration object with:
		 *   - chartData: Transformed dataset (percentage or raw)
		 *   - chartType: 'BarChart'
		 *   - chartOptions: Merged Chart.js options
		 *   Returns null if no chart data is provided
		 */
		barChart () {
			if (!this.chart) return this.chart

			// Get chart data, converting to percentage if needed
			const chartData = this.isPercentageMode
				? this.convertToPercentage(this.chart.chartData)
				: this.chart.chartData

			// Theme-aware grid and tick colours
			const isDark = this.$vuetify?.theme?.dark || false
			const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
			const tickColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'

			// Clone chart and force to BarChart with stacking
			// Remove yPrimary scale to prevent double Y-axis
			// Merge with incoming chartOptions to preserve legend settings etc.
			const incomingOptions = this.chart.chartOptions || {}
			const modifiedChart = {
				...this.chart,
				chartData,
				chartType: 'BarChart',
				chartOptions: {
					...incomingOptions,
					responsive: true,
					maintainAspectRatio: false,
					scales: {
						x: {
							stacked: true,
							ticks: {
								color: tickColor,
								font: { size: 11 }
							},
							grid: {
								display: false
							},
							border: {
								display: false
							}
						},
						y: {
							stacked: true,
							beginAtZero: true,
							max: this.isPercentageMode ? 100 : undefined,
							ticks: this.isPercentageMode
								? {
									color: tickColor,
									font: { size: 11 },
									callback: value => `${value}%`
								}
								: {
									color: tickColor,
									font: { size: 11 }
								},
							grid: {
								color: gridColor,
								drawBorder: false
							},
							border: {
								display: false
							}
						}
					},
					elements: {
						bar: {
							borderRadius: 4
						}
					},
					plugins: {
						...(incomingOptions.plugins || {}),
						tooltip: {
							callbacks: {
								label: context => {
									const value = context.parsed.y
									const label = context.dataset.label || ''
									if (this.isPercentageMode) {
										return `${label}: ${value.toFixed(1)}%`
									}
									return `${label}: ${value.toLocaleString()}`
								}
							}
						}
					}
				}
			}

			return modifiedChart
		},

		/**
		 * Generate array of colours for datasets using getChartColorByIndex
		 * References colorVersion to trigger re-computation when custom colours change
		 * @returns {string[]} Array of hex colour codes
		 */
		colors () {
			// Reference colorVersion to trigger reactivity when colours are updated
			// eslint-disable-next-line no-unused-vars
			const _version = this.colorVersion

			if (!this.chart || !this.chart.chartData || !this.chart.chartData.datasets) {
				return []
			}

			return this.chart.chartData.datasets.map((_, index) =>
				getChartColorByIndex(index, this.palette)
			)
		},

		/**
		 * Derive pie data from time-series chart data
		 * @returns {Object[]} Array of pie data objects with label, value, percentage, and colour
		 */
		pieData () {
			if (!this.chart || !this.chart.chartData) {
				return []
			}

			return derivePieData(this.chart.chartData, this.colors, {
				threshold: 0.01,
				maxItems: 5
			})
		},

		/**
		 * Convert pie data to Chart.js format
		 * @returns {Object} Chart.js compatible pie chart data object
		 */
		pieChartData () {
			return toPieChartFormat(this.pieData)
		},

		/**
		 * Chart.js options for the pie chart with legend at bottom and percentage tooltips
		 * @returns {Object} Chart.js options configuration
		 */
		pieChartOptions () {
			return {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						position: 'bottom',
						labels: {
							usePointStyle: true,
							padding: 15,
							font: {
								size: 11
							}
						}
					},
					tooltip: {
						callbacks: {
							label: context => {
								const item = this.pieData[context.dataIndex]
								if (!item) return ''
								return `${item.label}: ${item.value.toLocaleString()} (${item.percentage.toFixed(1)}%)`
							}
						}
					}
				}
			}
		},

		/**
		 * Check if pie chart has valid data to display
		 * @returns {boolean} True if pie data exists and has items
		 */
		hasPieData () {
			return this.pieData && this.pieData.length > 0
		},

		/**
		 * Generate title for the pie chart based on the time-series chart title
		 * @returns {string} Pie chart title like "New Data Subjects - Distribution"
		 */
		pieChartTitle () {
			if (!this.chart || !this.chart.title) {
				return 'Distribution'
			}

			// If title already contains "Distribution", just return it
			if (this.chart.title.toLowerCase().includes('distribution')) {
				return this.chart.title
			}

			// Otherwise append "- Distribution"
			return `${this.chart.title} - Distribution`
		}
	},
	methods: {
		/**
		 * Convert chart data to percentage values per time period
		 * Each bar segment becomes a percentage of the total for that time period
		 * @param {Object} chartData - Original chart data with datasets
		 * @returns {Object} Chart data with percentage values
		 */
		convertToPercentage (chartData) {
			if (!chartData || !chartData.datasets || !chartData.labels) {
				return chartData
			}

			// Calculate totals for each time period (label)
			const totals = chartData.labels.map((_, labelIndex) => {
				return chartData.datasets.reduce((sum, dataset) => {
					const value = dataset.data[labelIndex] || 0
					return sum + value
				}, 0)
			})

			// Convert each dataset's values to percentages
			const percentageDatasets = chartData.datasets.map(dataset => ({
				...dataset,
				data: dataset.data.map((value, index) => {
					const numValue = Number(value) || 0 // Consistent null/undefined handling
					const total = totals[index]
					if (total === 0) return 0
					return Number(((numValue / total) * 100).toFixed(2)) // Round to 2 decimals
				})
			}))

			return {
				...chartData,
				datasets: percentageDatasets
			}
		}
	}
}
</script>

<style scoped>
/* Dashboard row - spacing controlled by parent */
.dashboard-row {
	/* margin controlled via class on parent */
}

/* Pie chart container - fills available space */
.pie-chart-container {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.toggle-symbol {
	font-weight: 600;
	font-size: 0.875rem;
}
</style>

<style>
/* View mode toggle styling - unscoped for Vuetify components */
.view-mode-toggle.v-btn-toggle {
	background-color: var(--global-card-background, #ffffff);
	border-radius: 6px;
	border: 1px solid var(--global-border-color, #e2e8f0);
	margin-right: 8px;
}

.view-mode-toggle.v-btn-toggle .v-btn {
	min-width: 32px !important;
	width: 32px !important;
	height: 26px !important;
	padding: 0 !important;
	border-radius: 4px !important;
	border: none !important;
	box-shadow: none !important;
	margin: 0 !important;
	letter-spacing: normal !important;
	background-color: transparent !important;
	color: var(--global-muted-foreground, #64748b) !important;
	opacity: 1 !important;
}

.view-mode-toggle.v-btn-toggle .v-btn::before {
	opacity: 0 !important;
}

.view-mode-toggle.v-btn-toggle .v-btn.v-btn--active,
.view-mode-toggle.v-btn-toggle .v-btn.v-item--active {
	background-color: var(--v-primary-base, var(--global-primary, #406A94)) !important;
	color: white !important;
}

.view-mode-toggle.v-btn-toggle .v-btn.v-btn--active .toggle-symbol,
.view-mode-toggle.v-btn-toggle .v-btn.v-item--active .toggle-symbol {
	color: white !important;
}

.view-mode-toggle.v-btn-toggle .v-btn:not(.v-btn--active):not(.v-item--active):hover {
	background-color: var(--global-hover-background, rgba(0, 0, 0, 0.04)) !important;
}
</style>
